import type { NarrativeForm } from './schema';
import { getCompanyFinancialData, getDefaultValuatorData } from './calculation';
import type { NarrativeValuatorCompany } from '../hooks/narrativeValuatorCompanyFragment';
import type { CreateValuationInput } from '../hooks/useCreatePublicNarrative';
import type { UpdateValuationInput } from '../hooks/useUpdatePublicNarrative';
import { ZodEffects, ZodTypeAny, z } from 'zod';
import { INPUT_VALUE_RANGES } from './validation';

type TransformFormValuesToValuationProps = {
  values: NarrativeForm;
};

export const transformFormValuesToValuation = ({
  values,
}: TransformFormValuesToValuationProps) => {
  const {
    discountRatePercentage,
    fairValue,
    priceEarningsMultiple,
    profitMarginPercentage,
    revenuePercentage,
    timePeriod,
    futureSharesOutstanding,
  } = values;
  return {
    discountRate: discountRatePercentage,
    fairValue,
    futurePePercentageChange: priceEarningsMultiple,
    profitMarginPercentageChange: profitMarginPercentage,
    revenuePercentageChange: revenuePercentage,
    futureSharesOutstanding,
    timeframe: timePeriod,
  } satisfies CreateValuationInput | UpdateValuationInput;
};

export const transformValuationToFormValues = (
  valuation: {
    fairValue: number | null;
    futureSharesOutstanding: number | null;
    discountRate: number | null;
    futurePePercentageChange: number | null;
    profitMarginPercentageChange: number | null;
    revenuePercentageChange: number | null;
    timeframe: number | null;
    exchangeRate: number;
  },
  company: NarrativeValuatorCompany
): Pick<
  NarrativeForm,
  | 'fairValue'
  | 'futureSharesOutstanding'
  | 'discountRatePercentage'
  | 'priceEarningsMultiple'
  | 'profitMarginPercentage'
  | 'revenuePercentage'
  | 'timePeriod'
  | 'exchangeRate'
> => {
  const companyFinancialData = getCompanyFinancialData(company);

  const defaultValuatorData = getDefaultValuatorData({
    ...companyFinancialData,
    fairValue: valuation.fairValue ?? 0,
    estimateRevenueGrowth: true,
    exchangeRate: valuation.exchangeRate,
  });

  return {
    /**
     * Fair value can be null for APT/DCF valuations, that is why GQL type has it as nullable.
     * However, these valuation types aren't editable so this defaulting should technically never occur.
     */
    fairValue: defaultValuatorData.fairValue,
    /**
     * Legacy fair values can miss the following fields which were only introduced
     * when we introduced a full page narrative experience with these fields being configurable by the user/backsolve.
     * Prior to this, users could only input fairValue.
     */
    futureSharesOutstanding:
      valuation.futureSharesOutstanding ??
      defaultValuatorData.futureSharesOutstanding,
    discountRatePercentage:
      valuation.discountRate ?? defaultValuatorData.discountRatePercentage,
    priceEarningsMultiple:
      valuation.futurePePercentageChange ??
      defaultValuatorData.priceEarningsMultiple,
    profitMarginPercentage:
      valuation.profitMarginPercentageChange ??
      defaultValuatorData.profitMarginPercentage,
    revenuePercentage:
      valuation.revenuePercentageChange ??
      defaultValuatorData.revenuePercentage,
    timePeriod: valuation.timeframe ?? defaultValuatorData.timePeriod,
    exchangeRate: defaultValuatorData.exchangeRate,
  };
};

/**
 * Map of NarrativeForm fields to query params
 * "i" stands for initial, the rest tries to express the field name in a condensed way
 */
export const NARRATIVE_VALUATOR_QUERY_FIELD_MAP = {
  fairValue: {
    name: 'ifv',
    schema: z.preprocess(
      (val) => (typeof val === 'string' ? parseFloat(val) : null),
      z.number().min(0).max(Number.MAX_SAFE_INTEGER)
    ),
  },
  revenuePercentage: {
    name: 'irp',
    schema: z.preprocess(
      (val) => (typeof val === 'string' ? parseFloat(val) : null),
      z
        .number()
        .min(INPUT_VALUE_RANGES.revenuePercentage.min)
        .max(INPUT_VALUE_RANGES.revenuePercentage.max)
    ),
  },
  timePeriod: {
    name: 'itp',
    schema: z.preprocess(
      (val) => (typeof val === 'string' ? parseInt(val, 10) : null),
      z.union([z.literal(3), z.literal(5), z.literal(10)])
    ),
  },
  title: {
    name: 'iti',
    schema: z.string(),
  },
} satisfies Partial<
  Record<
    keyof NarrativeForm,
    { name: string; schema: ZodTypeAny | ZodEffects<ZodTypeAny> }
  >
>;

export const narrativeGetValuatorQueryParamInitialValues =
  (): Partial<NarrativeForm> => {
    if (typeof window === 'undefined') return {};
    const url = new URL(window.location.href);

    const values = {};
    for (const [fieldName, metadata] of Object.entries(
      NARRATIVE_VALUATOR_QUERY_FIELD_MAP
    )) {
      const queryValue = url.searchParams.get(metadata.name);
      if (!queryValue) continue;

      values[fieldName] =
        metadata.schema.safeParse(queryValue).data ?? undefined;
    }

    return values;
  };
